//Library Imports
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
//Local Imports
import { RootState } from "store";
import EmailList from "../EmailList";
// actions Imports
import {
  addNode,
  clearDropArea,
  addActionModal,
  editNode,
  addWFEmail,
  removeWFEmail,
  setSelectedEmailIdAction,
} from "../../actions";
import { ControlNameEnum } from "../../interfaces";
import { findDropAreaData } from "../../utils";
//Style Imports
import styled from "styled-components";

type ISendEmailProps = {
  id: string;
  enableSaveButton: (value: boolean) => void;
};

const SendEmail = (props: ISendEmailProps) => {
  // Props
  const { id, enableSaveButton } = props;

  // Redux States
  const itemData = useSelector((state: RootState) => state.automation.workflow.flow[id]?.detail);
  const emailData = useSelector((state: RootState) => state.automation.emails.data);
  const shareProfileTemplates = useSelector((state: RootState) => state.automation.emails.shareProfileTemplates);
  const modal = useSelector((state: RootState) => state.automation.modals.addAction);
  const actionModalVisible: boolean = useSelector((state: RootState) => state.automation.modals.addAction.visible);
  const triggerId: number = useSelector((state: RootState) => state.automation.workflow.detail.triggerId);

  // Local States
  const [selectedEmail, setSelectedEmail] = useState({
    id: 0,
    title: "",
  });

  // Local Variables
  const dispatch = useDispatch();

  // Component Logic
  useEffect(() => {
    if (selectedEmail?.id === 0) {
      enableSaveButton(false);
    } else {
      enableSaveButton(true);
    }
    return () => {
      enableSaveButton(false);
    };
  }, []);

  // useEffect to set the data of edit Node
  const isEmailModalOpen = modal.visible === true && modal.tab === ControlNameEnum.SEND_EMAIL;
  let dependencies = itemData && isEmailModalOpen;
  useEffect(() => {
    if (isEmailModalOpen && itemData) {
      if (itemData.hasOwnProperty("emailId")) {
        setSelectedEmail({
          id: itemData.emailId,
          title: itemData.emailName,
        });
        setSelectedEmailIdAction(itemData.emailId);
        enableSaveButton(true);
      }
    } else if (isEmailModalOpen && selectedEmail?.id !== 0) {
      setSelectedEmail({ id: 0, title: "" });
      setSelectedEmailIdAction(0);
      enableSaveButton(false);
    }
  }, [dependencies]);

  const closeModal = () => {
    dispatch(addActionModal(false));
    dispatch(clearDropArea());
  };

  const submitHandler = (event: any) => {
    event.preventDefault();
    let obj = findDropAreaData(ControlNameEnum.SEND_EMAIL);
    let title = `: ${selectedEmail?.title}`;
    if (id) {
      // edit Node
      dispatch(
        editNode(id, {
          ...itemData,
          emailId: selectedEmail?.id,
          emailName: selectedEmail?.title,
          nodeTitle: title,
        })
      );
      dispatch(removeWFEmail(itemData?.emailId));
    } else {
      // add node
      dispatch(
        addNode({
          ...obj,
          emailId: selectedEmail?.id,
          emailName: selectedEmail?.title,
          nodeTitle: title,
          emailType: triggerId === 10 ? "shared profile" : "email", // 10 is for shared email
        })
      );
    }
    dispatch(addWFEmail(selectedEmail?.id));
    setSelectedEmail({
      id: 0,
      title: "",
    });
    setSelectedEmailIdAction(0);
    closeModal();
  };

  const changeSelectedEmail = (id: number) => {
    const email = emailData.find((email) => email.emailId === id);
    const template = shareProfileTemplates.find((template) => template.id === id);
    if (email || template) enableSaveButton(true);
    setSelectedEmail({
      id: email ? email?.emailId : template ? template.id : 0,
      title: email ? email?.title : template ? template.name : "",
    });
    setSelectedEmailIdAction(id);
  };

  useEffect(() => {
    if (!actionModalVisible) {
      setSelectedEmail({
        id: 0,
        title: "",
      });
      setSelectedEmailIdAction(0);
    }
  }, [actionModalVisible]);

  return (
    <Wrapper>
      <div className="d-flex align-items-start">
        <EmailList
          selectedEmailId={selectedEmail?.id}
          changeHandler={changeSelectedEmail}
          isActionModal
          isEditMode={!!id}
          nodeId={id}
          submitHandler={submitHandler}
          isWorkflow
        />
      </div>
    </Wrapper>
  );
};

export default SendEmail;

const Wrapper = styled.div`
  .overflow-scroll {
    max-height: 570px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      max-height: 490px;
    }
  }
  .done-button {
    margin-top: -60px;
    padding-bottom: 20px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: -47px;
    }
  }
  .trigger-sidebar {
    border-right: 1px solid #dddddd;
    width: 265px;
    height: calc(100vh - 290px);
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      min-height: 550px;
    }
    h4 {
      color: #424b50;
      font-size: 20px;
      font-weight: 500;
      font-family: "Roboto", sans-serif;
      margin: 22px 24px 10px 24px;
    }
    .tabs-item {
      padding: 12px 0 8px 27px;
      background-color: #e6f5ea;
      width: 100%;
      color: #424b50;
      font-size: 16px;
      font-weight: 500;
      font-family: "Roboto", sans-serif;
      position: relative;
      &:before {
        content: "";
        width: 12px;
        height: 100%;
        background-color: #1ec252;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }
`;
